import { RefreshCw } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../lib/shadcn/alert-dialog'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useGenerateAllocation } from '../../hooks/backend/fleet'

type Summary = {
  drivers_placed?: number
  drivers_unplaced?: number
  cars_with_3?: number
  cars_with_2?: number
  cars_with_1?: number
  manual_seats_kept_on_same_car?: number
  crews_exceeding_commute_rule?: number
}

const n = (v: number | undefined): number => (typeof v === 'number' ? v : 0)

// Runs a fresh allocation through generate_allocation_v11, then refreshes the
// page data and reports the summary the function returns. This is the modern
// replacement for the old edge-function refresh.
export function ReallocateButton({ onReallocated }: { onReallocated: () => void }) {
  const generate = useGenerateAllocation()

  async function handleReallocate() {
    try {
      const summary = (await generate.trigger({}).result) as Summary
      onReallocated()
      toast.success('Allocation run complete', {
        description:
          `${n(summary.drivers_placed)} placed, ${n(summary.drivers_unplaced)} unplaced. ` +
          `Cars: ${n(summary.cars_with_3)} with 3, ${n(summary.cars_with_2)} with 2, ` +
          `${n(summary.cars_with_1)} with 1. ` +
          `${n(summary.manual_seats_kept_on_same_car)} manual seats kept on the same car; ` +
          `${n(summary.crews_exceeding_commute_rule)} crews exceed the commute rule.`,
      })
    } catch (err) {
      // Surface the message the function raised, verbatim.
      toast.error('Allocation run failed', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 gap-1.5" disabled={generate.loading}>
          {generate.loading ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {generate.loading ? 'Re-allocating...' : 'Re-allocate'}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Run a fresh allocation?</AlertDialogTitle>
          <AlertDialogDescription>
            This builds a new allocation run for the week. No car is left below two crew while drivers
            remain, and manual seat changes are kept on the same car. It can take a moment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={generate.loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault()
              void handleReallocate()
            }}
            disabled={generate.loading}
          >
            Re-allocate
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
